export function getActiveBranch() {
  return this.getRootState('branch') || 'main'
}

export function getActiveVersion() {
  return this.getRootState('version')
}

export function getLatestVersion() {
  return this.getRootState('latestVersion')
}

export async function setActiveBranch(branch = 'main') {
  if (branch === 'new-branch') {
    return await this.call('openRouterModal', '/add-branch')
  }

  const rootState = this.getRootState()
  const previousBranch = rootState.branch
  const previousVersion = rootState.version

  rootState.quietUpdate({ loading: true, branch, version: null })

  window.requestAnimationFrame(async () => {
    try {
      // const data = await this.sdk.switchBranch(branch)
      const data = await this.sdk.getProjectData(this.getRootState().projectId, { branch })

      // Validate data before syncing
      if (!data || Object.keys(data).length === 0) {
        throw new Error('Received empty data from branch switch')
      }

      this.call('sync', data, (synced) => {
        if (!synced) {
          // Sync failed, restore previous state
          rootState.update({
            loading: false,
            branch: previousBranch,
            version: previousVersion
          })
          return
        }

        rootState.replace({ ...synced, loading: false })
      })
    } catch (error) {
      // Restore previous branch state
      rootState.update({
        loading: false,
        branch: previousBranch,
        version: previousVersion
      })
      console.error('Failed to switch branch:', error)

      this.call('openNotification', {
        type: 'error',
        message: this.call('polyglot', 'editor.project.branchSwitchFailed')
          .replace('{branch}', branch)
          .replace('{error}', error.message)
      })
    }
  })

  this.getCanvas().lookup('Versions').update()
}

export async function setActiveVersion(version) {
  const rootState = this.getRootState()
  // Skip if already on the requested version
  if (rootState.version === version || (version === 'latest' && !rootState.isOld)) {
    return
  }

  const previousVersion = rootState.version
  const previousIsOld = rootState.isOld

  // Indicate loading state & whether we are on an old version
  rootState.quietUpdate({ isOld: version !== 'latest' })

  // Ensure at least one awaited expression so ESLint's require-await rule passes
  await new Promise((resolve) => {
    window.requestAnimationFrame(resolve)
  })

  try {
    const data = await this.sdk.getProjectData(rootState.projectId, {
      version
    })

    // Validate the received payload
    if (!data || Object.keys(data).length === 0) {
      throw new Error('Received empty data from version switch')
    }

    // Sync freshly received data with the current state first so that we keep
    // socket/client information intact. Once synced – replace the root state
    // to trigger full re-render.
    this.call('sync', data, (synced) => {
      if (!synced) {
        // Sync failed – rollback to previous state
        rootState.update({
          version: previousVersion,
          isOld: previousIsOld
        })
        return
      }

      rootState.replace({
        ...synced,
        version,
        isOld: version !== 'latest'
      })
    })
  } catch (error) {
    // Rollback to the previous state on error
    rootState.replace({
      version: previousVersion,
      isOld: previousIsOld
    })

    console.error('Failed to switch version:', error)

    this.call('openNotification', {
      type: 'error',
      message: this.call('polyglot', 'editor.project.versionSwitchFailed')
        .replace('{version}', version)
        .replace('{error}', error.message)
    })
  }

  // Force Versions sidebar to refresh its UI state
  // this.getCanvas().lookup('Versions').update()
}

export function getActiveProjectName() {
  return this.getRootState('projectName')
}

export function getRootProjects() {
  const projects = this.getRootState()?.projects || []
  // Remove duplicates based on project id (This should be just a workaround)
  return [...new Map(projects.map((project) => [project.id, project])).values()]
}

export function getRootProjectsUsers() {
  return this.getRootState()?.projectUsers
}

export function getRootProjectId() {
  return this.getRootState()?.projectId
}

// Shared function that handles the common project switching logic
async function performProjectSwitch(context, fullProjectData, projectId, projectRole) {
  const { projects } = context.getRootState()

  // Reconstruct the "basicProject" shape from the new payload so that
  // everything assigned into rootState stays the same as before.
  const projectInfo = fullProjectData.projectInfo || {}
  const projectMeta = fullProjectData.projectMeta || {}

  const basicProject = {
    // Core project metadata
    ...projectMeta,
    ...projectInfo,
    // Relations,
    members: fullProjectData.members,
    owner: fullProjectData.owner,
    // Shared libraries info,
    sharedLibraries: fullProjectData.sharedLibraries || []
  }

  const { key: appKey, name: projectName, id } = basicProject
  const effectiveProjectId = id || projectId

  // Keep SDK context update semantics the same, but use reconstructed basicProject
  await context.call('updateSDKContext', {
    project: basicProject,
    appKey,
    projectRole
  })

  context.call('applyLoader', { value: true, force: true, timeout: 1 })

  const state = context.getRootState()
  state.clients = { localhost: {}, cli: {}, platform: {} }
  state.settings = {}
  state.project = {}
  state.canvas = {
    pages: {},
    clients: {},
    gridOptions: {
      pages: [],
      comp: [],
      atom: [],
      children: []
    }
  }

  // Use the full project data as the "loadedProjectData" part
  const loadedProjectData = fullProjectData
  const projectData = {
    ...basicProject,
    ...loadedProjectData,
    ...(appKey && { appKey }),
    ...(effectiveProjectId && { projectId: effectiveProjectId }),
    ...(projectName && { projectName }),
    ...(projectRole && { projectRole }),
    sharedLibraries: basicProject.sharedLibraries || loadedProjectData.sharedLibraries || []
  }

  // USER-CODE-ORIGIN review N4: stored AS IS (strings) — the shell never
  // revives member-authored project code; the opaque frame does.
  context.call('overwrite', state, projectData)

  // If we're on canvas page, connect to new project's collaboration room
  if (context.isCanvas()) {
    await context.call('switchCanvasProject', {
      projectId: effectiveProjectId,
      branch: 'main',
      pro: state.pro || state.userData?.pro
    })
  }

  // Show loader until scene is ready
  context.call('applyLoader', { value: true, force: true, timeout: 35 })

  return state
}

export async function switchProject(projectId) {
  const { projects } = this.getRootState()

  // New backend returns everything we need in a single getProjectData() call
  const fullProjectData = await this.sdk.getProjectData(projectId)

  // Get the project role with fallback to API call if not found in user projects
  const effectiveProjectId =
    fullProjectData.projectInfo?.id || fullProjectData.projectMeta?.id || projectId
  const projectRole = await this.sdk.getProjectRoleWithFallback(effectiveProjectId, projects)

  await performProjectSwitch(this, fullProjectData, projectId, projectRole)

  this.router(this.getCanvasPathname() || this.fallbackRoute || '/canvas')
}

export async function switchProjectByKey(projectRef) {
  // Switch the active project by `owner/key` (or `{ owner, key }`), without
  // relying on state.activeProject. A bare key can name several owners'
  // projects: the server answers it 409 `ambiguous_key` with the candidates,
  // and the user is told which owner/key pairs share the key — never a pick.
  if (!projectRef) {
    return
  }

  const raw = typeof projectRef === 'object' ? projectRef : String(projectRef)
  const at = typeof raw === 'string' ? raw.indexOf('/') : -1
  const ref =
    typeof raw === 'object'
      ? { owner: raw.owner || null, key: raw.key }
      : at > 0
        ? { owner: raw.slice(0, at), key: raw.slice(at + 1) }
        : { owner: null, key: raw }
  if (!ref.key) {
    return
  }
  const label = ref.owner ? `${ref.owner}/${ref.key}` : ref.key

  const rootState = this.getRootState()
  const { projects = [] } = rootState

  // 1. Fetch full project data by owner + key (contains everything basicProject used to have)
  let fullProjectData
  try {
    fullProjectData = await this.sdk.getProjectDataByKey(
      ref.owner ? { owner: ref.owner, key: ref.key } : ref.key
    )
  } catch (error) {
    const candidates = this.call('ambiguousKeyCandidates', error)
    if (!candidates) throw error
    this.openNotification({
      type: 'error',
      title: this.call('polyglot', 'editor.project.ambiguousKey.title'),
      message: this.call('polyglot', 'editor.project.ambiguousKey.message')
        .replace('{key}', ref.key)
        .replace('{candidates}', candidates.map((c) => `${c.owner}/${c.key}`).join(', '))
    })
    return
  }
  if (!fullProjectData) {
    this.warn(`[switchProjectByKey] Project data not found for key: ${label}`)
    this.openNotification({
      type: 'error',
      title: this.call('polyglot', 'editor.project.notFound.title'),
      message: this.call('polyglot', 'editor.project.notFound.message').replace('{key}', label)
    })
    return
  }

  const projectId = fullProjectData.projectInfo?.id || fullProjectData.projectMeta?.id

  // 2. Determine current user role by project id (never by a bare key) with
  // fallback to the API
  const projectRole = projectId
    ? await this.sdk.getProjectRoleWithFallback(projectId, projects)
    : null

  await performProjectSwitch(this, fullProjectData, projectId, projectRole)

  return fullProjectData
}
